import { Accordion, chakra } from '@chakra-ui/react';
import { Reorder, useDragControls } from 'framer-motion';
import { SlideItem } from './SlideItem';

import { Dispatch, PointerEventHandler, SetStateAction, useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';
import { unitAssessmentState } from '../UnitAssessmentState';
import { SlideKind } from '@duke-hq/svc-api-db';

import type { UnitAssessmentSlides } from '../types';

type AssessmentSlidesPropTypes = {
  slides: UnitAssessmentSlides;
  handleSlideDelete: (id: string) => void;
  handleDeleteMultiChoiceQuestion: (id: string) => void;
  setSlides: Dispatch<SetStateAction<any>>;
  saveReorder: () => void;
};

const List = chakra(Reorder.Group);
const ListItem = chakra(Reorder.Item);

export function AssessmentSlides({
  slides,
  handleSlideDelete,
  handleDeleteMultiChoiceQuestion,
  setSlides,
  saveReorder,
}: AssessmentSlidesPropTypes) {
  const unitAssessmentSnap = useSnapshot(unitAssessmentState);
  const { newlyCreatedSlideId } = unitAssessmentSnap;
  const [expandedIndices, setExpandedIndices] = useState<number[]>([]);

  // Some slide types we should expand on creation so the user can edit it right away
  useEffect(() => {
    if (newlyCreatedSlideId !== null && slides.length > 0) {
      const newSlideIndex = slides.findIndex(({ id }) => id === newlyCreatedSlideId);
      const newSlide = slides[newSlideIndex];

      if (newSlide !== undefined) {
        const expandableKinds = new Set<SlideKind>([SlideKind.FORM, SlideKind.QUESTION_GROUP]);
        if (newSlide.kind !== null && expandableKinds.has(newSlide.kind)) {
          setExpandedIndices([newSlideIndex]);
        }
        unitAssessmentState.setNewlyCreatedSlideId(null);
      }
    }
  }, [newlyCreatedSlideId, slides, setExpandedIndices]);

  return (
    <List values={slides} onReorder={setSlides} listStyleType="none">
      <Accordion
        allowMultiple
        index={expandedIndices}
        onChange={(indices) => setExpandedIndices(Array.isArray(indices) ? indices : [indices])}
      >
        {slides.map((slide) => (
          <DraggableSlideItem
            key={slide.id}
            slide={slide}
            saveReorder={saveReorder}
            handleSlideDelete={handleSlideDelete}
            handleDeleteMultiChoiceQuestion={handleDeleteMultiChoiceQuestion}
          />
        ))}
      </Accordion>
    </List>
  );
}

function DraggableSlideItem({
  slide,
  saveReorder,
  handleSlideDelete,
  handleDeleteMultiChoiceQuestion,
}: {
  slide: UnitAssessmentSlides[0];
  saveReorder: () => void;
  handleSlideDelete: (id: string) => void;
  handleDeleteMultiChoiceQuestion: (id: string) => void;
}) {
  const dragControls = useDragControls();

  const onStartDrag: PointerEventHandler<HTMLButtonElement> = (e) => {
    dragControls.start(e, { snapToCursor: true });
  };

  return (
    <ListItem
      value={slide}
      dragControls={dragControls}
      dragListener={false}
      onDragEnd={saveReorder}
      position="relative"
      data-position={slide.position}
    >
      <SlideItem
        slide={slide}
        handleSlideDelete={handleSlideDelete}
        handleDeleteMultiChoiceQuestion={handleDeleteMultiChoiceQuestion}
        onStartDrag={onStartDrag}
      />
    </ListItem>
  );
}
